import { useMoviesStore } from "@multicines/stores";
import { Session, SessionType as ISessionType } from "@multicines/types";
import dayjs from "dayjs";
import EmptyMovieSVG from "images/empty-movie.svg";
import { useRouter } from "next/router";
import React, { useCallback, useMemo } from "react";

import Styles from "./NowFilter.styles";
import { NowFilterProps as Props } from "./NowFilter.types";
import ScheduleSession from "../../global/ScheduleSession/ScheduleSession";
import EmptyState from "components/global/EmptyState/EmptyState";
import MovieCard from "components/global/MovieCard/MovieCard";
import SessionType from "components/global/SessionType/SessionType";
import useCountDown from "hooks/useCountDown";
import useI18n from "hooks/useI18n";
import { useGlobalsStore } from "stores/globals/globals.store";

const NowFilter: React.FC<Props> = props => {
  const { movie, sessionTypes, isFetching } = props;
  const { push } = useRouter();
  const setModalityFilter = useGlobalsStore(state => state.setModalityFilter);
  const setSelectedMovie = useMoviesStore(store => store.setSelectedMovie);
  const t = useI18n();
  const { externalId } = movie;

  const moviesFilter = useMemo(() => {
    return sessionTypes
      ?.filter(item => item.externalId === externalId)
      .map(movie => movie?.todaySessions)
      .flat();
  }, [externalId, sessionTypes]);

  const sessionTypeCount = moviesFilter?.length ?? 0;

  const theaterFilter = useMemo(() => {
    return moviesFilter?.map(theater => theater?.theaterTypes).flat();
  }, [moviesFilter]);

  const sessionsFilter = useMemo(() => {
    return theaterFilter?.map(theater => theater?.sessions).flat();
  }, [theaterFilter]);

  const showTimeFilter = sessionsFilter
    ?.filter(item => dayjs().isBefore(dayjs(item?.showtime), "minutes"))
    ?.sort((a, b) => a?.showtime.localeCompare(b?.showtime ?? "") ?? 0)
    .flat();

  const noSessions =
    showTimeFilter?.length === 0 ? "NowFilter__no-content" : [];
  const sortedShowTimes = showTimeFilter?.map(item => item?.showtime);
  const showtime = sortedShowTimes?.[0];
  const time = useCountDown(showtime);

  const goToBillboard = () => {
    setModalityFilter("All");
    push("/billboard");
  };

  const renderItem = useCallback(
    (listItem: ISessionType, index: number) => {
      const sessionType = listItem;

      return (
        <SessionType
          className="NowFilter__sessionType"
          key={index}
          position={index}
          sessionType={sessionType}
          sessionTypeCount={sessionTypeCount}
          disabled={isFetching}
          isPremiere
          onClick={() => setSelectedMovie(movie)}
        />
      );
    },
    [isFetching, movie, sessionTypeCount, setSelectedMovie]
  );

  const renderItemMemoized = useMemo(() => renderItem, [renderItem]);

  return (
    <Styles className="NowFilter">
      <div className="NowFilter__movies">
        {movie ? (
          <MovieCard className="NowFilter__card" movie={movie} isSelected />
        ) : null}
        <div className={`NowFilter__content ${noSessions}`}>
          {showTimeFilter && showTimeFilter.length > 0 ? (
            <>
              <div className="NowFilter__row">
                <p className="NowFilter__nextFunction">
                  {`${t.filters.nextFunction} `}
                  {time}
                </p>
                <ScheduleSession
                  className="NowFilter__button"
                  session={showTimeFilter[0] as Session}
                  onClick={() => setSelectedMovie(movie)}
                />
              </div>
              {moviesFilter?.map((sessionType, index) => {
                return renderItemMemoized(sessionType as ISessionType, index);
              })}
            </>
          ) : (
            <EmptyState
              icon={<EmptyMovieSVG />}
              description={t.movie.emptyMovieDescription}
              buttonTitle={t.timeout.goToBillboard}
              onClick={goToBillboard}
            />
          )}
        </div>
      </div>
    </Styles>
  );
};

export default NowFilter;
